import { useScenarioFormContext } from '../contexts/ScenarioFormContext';
import { useRequest, useUnmount } from 'ahooks';
import {
  deleteBootProcessing,
  getBootProcessing,
  postBootProcessing,
  postConfigurationPolicy,
} from '../service';
import { message } from 'antd';
import { useTranslate } from '@tolgee/react';
import * as React from 'react';
import { removeCache, setCCV, useSubmitCCV } from './useConfiguration';
import { useCirCleContext } from '../contexts/CircleContext';
import { useStepName } from './useStepName';
import { ActivityCircle } from '../consts/step';
import { activityUnConfigStores, refreshInProcessState } from '../atoms/inProcess';
import { useUnConfigBootStoreContext } from '../contexts/UnConfigBootStoreContext';
import useLLM from './useLLM';

export const useBasicPostConfig = () => {
  const { value, cacheKey } = useScenarioFormContext();
  const { t } = useTranslate();
  const submitCCV = useSubmitCCV();
  const { loading, runAsync: _runAsync } = useRequest(
    (enable) => postConfigurationPolicy(value, enable),
    {
      manual: true,
      onError: () => {
        message.error(t('operation.saveFailed'));
      },
    },
  );

  React.useEffect(() => {
    setCCV(cacheKey, value.scenarios);
  }, [cacheKey, value.scenarios]);

  const unConfigStores = useUnConfigBootStoreContext();

  const LLM = useLLM();
  const stepName = useStepName();
  const { circle, type } = useCirCleContext();
  const nextRef = React.useRef(false);
  const { isFirstStep, isLastStep } = React.useMemo(() => {
    const isFirstStep = circle[0] === stepName;
    const isLastStep = circle.filter((_) => !ActivityCircle.includes(_)).pop() === stepName;
    return { isFirstStep, isLastStep };
  }, [circle, stepName]);
  useUnmount(() => {
    if (!nextRef.current && LLM && isFirstStep && !isLastStep) {
      getBootProcessing(true)
        .then((res) => {
          const target = res.find(({ configType }) => configType === 'AI_SALE_BOT');
          if (target) {
            const content = JSON.parse(target.content);
            if (
              Object.keys(content).every((key) => !key.startsWith('scenarioFormContext-BASIC-'))
            ) {
              return deleteBootProcessing(target.configurationId);
            }
          }
        })
        .catch(() => {});
    }
  });

  const runAsync = React.useCallback(
    (enable = true) => {
      if (LLM && isLastStep) {
        nextRef.current = true;
        activityUnConfigStores.current = unConfigStores;
        return postConfigurationPolicy(value, enable)
          .then(() => {
            if (type === 'marketing') {
              return;
            }
            return getBootProcessing(true).then((res) => {
              const target = res.find(({ configType }) => configType === 'AI_SALE_BOT');
              if (target) {
                return postBootProcessing({
                  ...target,
                  status: 'COMPLETED',
                  content: '',
                }).then(() => {
                  refreshInProcessState();
                  removeCache();
                });
              }
            });
          })
          .catch(() => {
            message.error(t('operation.saveFailed'));
            return Promise.reject();
          });
      }
      submitCCV(cacheKey, value.scenarios);
      return _runAsync(enable);
    },
    [submitCCV, cacheKey, value, _runAsync, LLM, isLastStep, t, unConfigStores, type],
  );

  return {
    loading,
    runAsync,
  };
};
